import { IAssistantConfig, IDiscountConfig, IPriceListItem, IPriceCondition, IPricingRuleDiscount, IQuestion, IQuestionOption } from "../types";

/**
 * Round a number to 2 decimal places (standard for currency)
 * Uses Math.round to ensure proper rounding (not truncation)
 */
const roundToTwoDecimals = (value: number): number => {
  return Math.round((value + Number.EPSILON) * 100) / 100;
};




/**
 * Applies margin percentage to a price.
 * Margin here is a share of the SELLING price, not a markup on cost:
 *   sellingPrice = costPrice / (1 - marginPercentage / 100)
 * e.g. a 20% margin on a £100 cost gives £125 (not £120 — that would be a
 * 20% markup on cost instead).
 *
 * @param price - Cost price (post-discount, if any discount applies)
 * @param marginPercentage - Margin % of the final selling price (e.g., 20 for 20%)
 * @returns Price with margin applied, rounded to 2 decimal places
 */
const applyMarginToPrice = (
  price: number | undefined | null,
  marginPercentage: number,
): any => {
  if (price === undefined || price === null || price === 0) {
    return price;
  }

  const divisor = 1 - marginPercentage / 100;
  // 100%+ margin is undefined under this formula (denominator hits 0 or goes
  // negative) — bail out and return the cost price unchanged rather than
  // producing Infinity/a negative price.
  if (divisor <= 0) return roundToTwoDecimals(price);

  return roundToTwoDecimals(price / divisor);
};





interface AccountOptionDiscount {
  discountPercentage: number;
  discountAmount: number;
  priceListDiscounts?: Array<{index: number; discountAmount: number; discountPercentage: number}>;
}


/**
 * Reduces a raw price by an account discount (flat amount takes priority over
 * percentage), floored at 0. Returns the raw price unchanged when there is no
 * discount to apply.
 */
const applyDiscountToPrice = (
  rawPrice: number | undefined | null,
  accountDiscount?: AccountOptionDiscount,
): number => {
  if (!rawPrice) return rawPrice ?? 0;
  if (!accountDiscount) return rawPrice;
  if (rawPrice < 0) return rawPrice;
  const discounted =
    accountDiscount.discountAmount > 0
      ? rawPrice - accountDiscount.discountAmount
      : rawPrice * (1 - accountDiscount.discountPercentage / 100);
  return roundToTwoDecimals(Math.max(0, discounted));
};
/**
 * Applies account discount then margin to a single option's CUSTOMER-facing
 * price. The VSS/cost price is deliberately left raw (undiscounted) — the
 * discount is tracked separately as `vssDiscountAmount`, exactly like before.
 * These are two independent price tracks: the customer-facing price bakes
 * the discount straight into the number; the VSS/cost-price track keeps the
 * full raw number plus a separate "how much was saved" figure, because VSS
 * reporting needs to show dealers the gross/discount/net breakdown, not just
 * a single already-net number.
 *
 * Order: Account Discount → Margin, customer price only. VSS price is never
 * discounted or margined.
 */
const applyMarginToOption = (
  option: IQuestionOption,
  marginPercentage: number,
  accountDiscount?: AccountOptionDiscount,
): IQuestionOption => {
  const vssBase = option.price || option.priceModifier || option.vssPrice || 0;

  // Fall back to the option's OWN embedded discountPercentage/discountAmount
  // (straight from the base config) when there's no per-account discountConfig
  // override for this option. Without this, applyTransforms silently applied
  // 0% discount whenever accountDiscount was undefined, while the discount
  // fields returned below (which DO fall back to option.discountPercentage)
  // kept reporting the option's real rate — so the UI showed "10% off" on a
  // price that never actually had any discount applied to it.
  const effectiveDiscount: AccountOptionDiscount | undefined =
    accountDiscount ??
    (((option.discountPercentage ?? 0) > 0 || (option.discountAmount ?? 0) > 0)
      ? { discountPercentage: option.discountPercentage ?? 0, discountAmount: option.discountAmount ?? 0 }
      : undefined);

  const applyTransforms = (price: number | undefined | null): any => {
    if (!price) return price ?? 0;
    return applyMarginToPrice(applyDiscountToPrice(price, effectiveDiscount), marginPercentage);
  };

  const vssDiscountAmount = vssBase < 0
    ? 0
    : effectiveDiscount
      ? (effectiveDiscount.discountAmount > 0
          ? roundToTwoDecimals(effectiveDiscount.discountAmount)
          : roundToTwoDecimals(vssBase * (effectiveDiscount.discountPercentage / 100)))
      : 0;

  // (value ?? 0) > 0, not !== undefined — config commonly sends a literal 0
  // as a placeholder for "no override", which would otherwise short-circuit
  // every priceList entry to "0% unglazed discount" and never fall through
  // to priceListDiscounts below.
  const unglazedDiscountDefined = (option.unglazedDiscountPercentage ?? 0) > 0 || (option.unglazedDiscountAmount ?? 0) > 0;
  // Per-account discountConfig override takes priority over the option's own
  // config-authored priceListDiscounts, same precedence as discountPercentage/
  // discountAmount above.
  const effectivePriceListDiscounts = accountDiscount?.priceListDiscounts ?? option.priceListDiscounts;

  const transformedPriceList: IPriceListItem[] | undefined = option.priceList?.map((item, i) => {
    // Priority for the discount applied to a priceList entry (this is the
    // option's UNGLAZED price track, per its priceList condition):
    //   1. unglazedDiscountPercentage/Amount, when the config defines one —
    //      the only purpose-built override for this exact scenario.
    //   2. Else priceListDiscounts[i] — looked up by this entry's OWN index
    //      `i` into THIS SAME option's own priceList/priceListDiscounts
    //      arrays, never a different option's array and never after
    //      reordering/filtering, so a discount authored for a different
    //      product's row can't leak onto this entry via a coincidental index
    //      match (the original bug this exclusion was guarding against).
    //   3. Else the account-level discount (discountConfig) — the per_window
    //      global discount (or the option-specific questionFlow discount). This
    //      ensures a per_window discount reaches priceList-resolved prices, not
    //      just the option's main price.
    //   4. Else no discount.
    // Only affects the CUSTOMER price; vssPrice per entry stays raw below.
    const plDiscount = effectivePriceListDiscounts?.find((d) => d.index === i);
    const discountForEntry: AccountOptionDiscount | undefined = unglazedDiscountDefined
      ? { discountPercentage: option.unglazedDiscountPercentage ?? 0, discountAmount: option.unglazedDiscountAmount ?? 0 }
      : plDiscount
        ? { discountPercentage: plDiscount.discountPercentage ?? 0, discountAmount: plDiscount.discountAmount ?? 0 }
        : accountDiscount;
    const discountedItemPrice = applyDiscountToPrice(item.price, discountForEntry);
    return {
      ...item,
      price: applyMarginToPrice(discountedItemPrice, marginPercentage) ?? discountedItemPrice,
      vssPrice: item.price ?? 0,
    };
  });
  return {
    ...option,
    price: applyTransforms(option.price),
    priceModifier: applyTransforms(option.priceModifier),
    vssPrice: vssBase,
    vssDiscountAmount,
    // preserve discount config so priceList-resolved breakdown can compute vssDiscountAmount
    // — kept in sync with effectiveDiscount so the displayed rate always matches
    // what was actually applied to price/priceModifier above.
    discountPercentage: effectiveDiscount?.discountPercentage ?? option.discountPercentage,
    discountAmount: effectiveDiscount?.discountAmount ?? option.discountAmount,
    priceListDiscounts: accountDiscount?.priceListDiscounts ?? option.priceListDiscounts,
    // Config-level unglazed discount override — account discount has no
    // unglazed-specific concept, so this always comes straight from config.
    unglazedDiscountPercentage: option.unglazedDiscountPercentage,
    unglazedDiscountAmount: option.unglazedDiscountAmount,
    priceList: transformedPriceList,
  };
};

/**
 * Applies margin percentage and VSS discount to all options in a question.
 * optionDiscountMap provides per-option account discounts keyed by optionId.
 */
const applyMarginToQuestion = (
  question: IQuestion,
  marginPercentage: number,
  optionDiscountMap?: Map<string, AccountOptionDiscount>,
): IQuestion => {
  return {
    ...question,
    options: question.options?.map((option) =>
      applyMarginToOption(option, marginPercentage, optionDiscountMap?.get(option.optionId)),
    ),
  };
};

/**
 * Applies margin percentage and VSS discount to the entire configuration
 *
 * Price Transformation Flow:
 * 1. VSS Discount - ONLY applied to products (optionType: 0 and 64)
 * 2. Margin - Applied to ALL options after VSS discount
 *
 * @param config - The conversation flow configuration
 * @param marginPercentage - The margin percentage to apply to all options (e.g., 15 for 15%)
 * @param vssDiscountPercentage - The VSS discount percentage to apply to products only (e.g., 10 for 10%)
 * @returns Configuration with transformations applied
 *
 * @example
 * ```typescript
 * const config = await getConfigurationJsonApi();
 * const marginPercentage = 15; // 15%
 * const vssDiscountPercentage = 10; // 10%
 * const configWithPricing = applyMarginToConfiguration(config, marginPercentage, vssDiscountPercentage);
 *
 * // Products (optionType: 0, 64): Original Price → VSS Discount (10%) → Margin (15%)
 * // Other options: Original Price → Margin (15%)
 * ```
 */
/**
 * For discountConfig.type === "per_window":
 * Applies the account-level discount to the VSS total at quote level.
 * This reduces what the dealer pays VS — the customer price is unaffected.
 *
 * Returns { discountedVssTotal, vssDiscountAmount }
 */
export const applyPerWindowVssDiscount = (
  vssTotalPrice: number,
  discountConfig: IDiscountConfig,
): { discountedVssTotal: number; vssDiscountAmount: number } => {
  if (discountConfig.type !== 'per_window') {
    return { discountedVssTotal: vssTotalPrice, vssDiscountAmount: 0 };
  }
  const discounted = discountConfig.discountAmount > 0
    ? Math.max(0, roundToTwoDecimals(vssTotalPrice - discountConfig.discountAmount))
    : discountConfig.discountPercentage > 0
      ? roundToTwoDecimals(vssTotalPrice * (1 - discountConfig.discountPercentage / 100))
      : vssTotalPrice;

  return {
    discountedVssTotal: discounted,
    vssDiscountAmount: roundToTwoDecimals(vssTotalPrice - discounted),
  };
};

/**
 * Merges pricingRuleDiscounts into pricingRules and every question/option
 * priceCondition — same pattern as discountConfig merges into option prices.
 */
function applyPricingRuleDiscounts(
  config: IAssistantConfig,
  pricingRuleDiscounts: IPricingRuleDiscount[],
): IAssistantConfig {
  const ruleDiscountMap = new Map(pricingRuleDiscounts.map(d => [d.ruleId, d]));
  const mergePC = (pc: IPriceCondition): IPriceCondition => {
    const d = ruleDiscountMap.get(pc.dogma_pricingruleid);
    return d ? { ...pc, discountPercentage: d.discountPercentage, discountAmount: d.discountAmount } : pc;
  };
  return {
    ...config,
    pricingRules: config.pricingRules?.map(mergePC),
    questionFlow: config.questionFlow?.map(q => ({
      ...q,
      priceConditions: q.priceConditions?.map(mergePC),
      options: q.options?.map(o => ({
        ...o,
        priceConditions: o.priceConditions?.map(mergePC),
      })),
    })),
  };
}

/**
 * Stamps the account-level per_window discount onto EVERY pricing rule and
 * question/option priceCondition, so surge charges receive the discount even
 * when `pricingRuleDiscounts` is empty. Specific `pricingRuleDiscounts` entries
 * are merged afterward (by applyPricingRuleDiscounts) and override this blanket
 * rate; rules without a specific entry keep the blanket per_window rate.
 */
function applyGlobalPricingRuleDiscount(
  config: IAssistantConfig,
  discountConfig: IDiscountConfig,
): IAssistantConfig {
  const { discountPercentage, discountAmount } = discountConfig;
  const stamp = (pc: IPriceCondition): IPriceCondition => ({
    ...pc,
    discountPercentage,
    discountAmount,
  });
  return {
    ...config,
    pricingRules: config.pricingRules?.map(stamp),
    questionFlow: config.questionFlow?.map(q => ({
      ...q,
      priceConditions: q.priceConditions?.map(stamp),
      options: q.options?.map(o => ({
        ...o,
        priceConditions: o.priceConditions?.map(stamp),
      })),
    })),
  };
}

export const applyMarginToConfiguration = (
  config: IAssistantConfig,
  marginPercentage: number,
  discountConfig?: IDiscountConfig,
  pricingRuleDiscounts?: IPricingRuleDiscount[],
): IAssistantConfig => {
  if (!config) return config;

  const withRuleDiscounts = (result: IAssistantConfig) =>
    pricingRuleDiscounts?.length ? applyPricingRuleDiscounts(result, pricingRuleDiscounts) : result;

  if (!discountConfig) {
    return withRuleDiscounts({
      ...config,
      questionFlow: config.questionFlow?.map((q) => applyMarginToQuestion(q, marginPercentage)),
    });
  }

  if (discountConfig.type === 'per_window') {
    const globalDiscount: AccountOptionDiscount = {
      discountPercentage: discountConfig.discountPercentage,
      discountAmount: discountConfig.discountAmount,
    };
    const EXCLUDED_TYPES = new Set(discountConfig.excludingWindowProduct ? [0, 7, 64] : []);
    const withQuestionFlow = {
      ...config,
      discountConfig,
      questionFlow: config.questionFlow?.map((q) => {
        const excluded = EXCLUDED_TYPES.has(q.optionType);
        return applyMarginToQuestion(
          q, marginPercentage,
          excluded ? undefined : new Map(q.options?.map((o) => [o.optionId, globalDiscount]) ?? []),
        );
      }),
    };
    // per_window discount also reaches pricing rules/surge charges: stamp the
    // blanket rate onto every pricing rule + priceCondition first, then let
    // specific pricingRuleDiscounts entries override it afterward.
    return withRuleDiscounts(applyGlobalPricingRuleDiscount(withQuestionFlow, discountConfig));
  }

  const optionDiscountMap = new Map<string, AccountOptionDiscount>();
  for (const qDiscount of discountConfig.questionFlow) {
    for (const opt of qDiscount.options) {
      if (opt.discountPercentage > 0 || opt.discountAmount > 0 || opt.priceListDiscounts?.length) {
        optionDiscountMap.set(opt.optionId, {
          discountPercentage: opt.discountPercentage,
          discountAmount: opt.discountAmount,
          priceListDiscounts: opt.priceListDiscounts,
        });
      }
    }
  }

  return withRuleDiscounts({
    ...config,
    discountConfig,
    questionFlow: config.questionFlow?.map((q) =>
      applyMarginToQuestion(q, marginPercentage, optionDiscountMap),
    ),
  });
};
